import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Filter,
  Plus,
  ChevronDown,
  ChevronUp,
  Download,
  Phone,
  Mail,
  MessageCircle,
  UserPlus,
  Calendar,
  Clock,
  StickyNote,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
} from "lucide-react";

// Firebase
import { app } from "../../src/Firebase-config";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

const db = getFirestore(app);

/** ======== CONFIG ======== */
const PAGE_SIZE = 25;
const STATUSES = [
  { key: "new", label: "Nuevo", color: "bg-sky-100 text-sky-700 border-sky-200" },
  { key: "contacted", label: "Contactado", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { key: "qualified", label: "Calificado", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { key: "trial", label: "En prueba", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "won", label: "Ganado", color: "bg-green-100 text-green-800 border-green-200" },
  { key: "lost", label: "Perdido", color: "bg-rose-100 text-rose-700 border-rose-200" },
  { key: "spam", label: "Spam", color: "bg-neutral-100 text-neutral-700 border-neutral-200" },
];

const statusMeta = Object.fromEntries(STATUSES.map(s => [s.key, s]));

/** ======== UTILS ======== */
function formatTS(ts) {
  if (!ts) return "-";
  const d = ts.toDate ? ts.toDate() : ts;
  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function toCSV(rows) {
  const headers = [
    "id",
    "nombre",
    "apellidos",
    "telefono",
    "correo",
    "direccion",
    "ciudad",
    "estado",
    "cp",
    "status",
    "owner",
    "nextActionAt",
    "lastActivityAt",
    "createdAt",
    "utm.source",
    "utm.medium",
    "utm.campaign",
    "utm.term",
    "utm.content"
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    const createdAt = r.createdAt?.toDate?.() || r.createdAt || "";
    const nextActionAt = r.nextActionAt?.toDate?.() || r.nextActionAt || "";
    const lastActivityAt = r.lastActivityAt?.toDate?.() || r.lastActivityAt || "";
    lines.push([
      r.id || "",
      r.nombre || "",
      r.apellidos || "",
      r.telefono || "",
      r.correo || "",
      r.direccion || "",
      r.ciudad || "",
      r.estado || "",
      r.cp || "",
      r.status || "",
      r.owner || "",
      createdAt ? createdAt.toISOString() : "",
      nextActionAt ? nextActionAt.toISOString() : "",
      lastActivityAt ? lastActivityAt.toISOString() : "",
      r.utm?.source || "",
      r.utm?.medium || "",
      r.utm?.campaign || "",
      r.utm?.term || "",
      r.utm?.content || "",
    ].map(escape).join(","));
  }
  return lines.join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function waLink(tel, msg = "") {
  if (!tel) return null;
  const clean = String(tel).replace(/[^\d+]/g, "");
  const encoded = encodeURIComponent(msg);
  return `https://wa.me/${clean.replace(/^\+/, "")}${msg ? `?text=${encoded}` : ""}`;
}

function mailLink(email, subject = "Seguimiento TiGo", body = "") {
  const s = encodeURIComponent(subject);
  const b = encodeURIComponent(body);
  return `mailto:${email}?subject=${s}&body=${b}`;
}

/** ======== COMPONENT ======== */
export default function TiGoLeadsCRM() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // filters
  const [qText, setQText] = useState("");
  const [qStatus, setQStatus] = useState("");
  const [qUTM, setQUTM] = useState(""); // source
  const [qDateFrom, setQDateFrom] = useState("");
  const [qDateTo, setQDateTo] = useState("");

  // quick reload anchor
  const unsubRef = useRef(null);

  const baseQuery = useMemo(() => {
    // Firestore no admite LIKE full; hacemos realtime + filtro en cliente para texto.
    // Sí filtramos por type y ordenamos por createdAt desc.
    return query(
      collection(db, "leads"),
      where("type", "==", "tigo_subscription"),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );
  }, []);

  useEffect(() => {
  setLoading(true);
  if (unsubRef.current) unsubRef.current();

  // Intento principal: where(type) + orderBy(createdAt)
  const qMain = query(
    collection(db, "leads"),
    where("type", "==", "tigo_subscription"),
    orderBy("createdAt", "desc"),
    limit(PAGE_SIZE)
  );

  unsubRef.current = onSnapshot(qMain, (snap) => {
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setItems(docs);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === PAGE_SIZE);
    setLoading(false);
  }, async (err) => {
    console.error("onSnapshot qMain error:", err?.code, err?.message);

    // Fallback si falta índice compuesto
    if (err?.code === "failed-precondition") {
      console.warn("No hay índice compuesto. Usando fallback temporal.");
      const qFallback = query(
        collection(db, "leads"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );
      const unsubFallback = onSnapshot(qFallback, (snap2) => {
        const all = snap2.docs.map(d => ({ id: d.id, ...d.data() }));
        // Filtra en cliente por type
        const only = all.filter(r => r.type === "tigo_subscription");
        setItems(only);
        setLastDoc(snap2.docs[snap2.docs.length - 1] || null);
        setHasMore(snap2.size === PAGE_SIZE);
        setLoading(false);
      }, (err2) => {
        console.error("onSnapshot fallback error:", err2?.code, err2?.message);
        setLoading(false);
      });
      unsubRef.current = unsubFallback;
    } else if (err?.code === "permission-denied") {
      alert("Permiso denegado: revisa que estés autenticado y tus reglas de Firestore.");
      setLoading(false);
    } else {
      setLoading(false);
    }
  });

  return () => {
    if (unsubRef.current) unsubRef.current();
  };
}, []);

  async function loadMore() {
    if (!lastDoc) return;
    const q2 = query(
      collection(db, "leads"),
      where("type", "==", "tigo_subscription"),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q2);
    const extra = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setItems(prev => [...prev, ...extra]);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === PAGE_SIZE);
  }

  // filtros en cliente (rápidos)
  const filtered = useMemo(() => {
    let list = items;

    if (qStatus) {
      list = list.filter(r => (r.status || "new") === qStatus);
    }
    if (qUTM) {
      const t = qUTM.toLowerCase();
      list = list.filter(r => (r.utm?.source || "").toLowerCase().includes(t));
    }
    if (qDateFrom) {
      const from = new Date(qDateFrom + "T00:00:00");
      list = list.filter(r => {
        const d = r.createdAt?.toDate?.() || r.createdAt || null;
        return d ? d >= from : false;
      });
    }
    if (qDateTo) {
      const to = new Date(qDateTo + "T23:59:59");
      list = list.filter(r => {
        const d = r.createdAt?.toDate?.() || r.createdAt || null;
        return d ? d <= to : false;
      });
    }
    if (qText.trim()) {
      const t = qText.toLowerCase();
      list = list.filter(r => {
        const fields = [
          r.nombre, r.apellidos, r.telefono, r.correo,
          r.ciudad, r.estado, r.cp, r.direccion,
          r.owner, r.utm?.source, r.utm?.campaign, r.utm?.medium
        ].map(x => (x || "").toString().toLowerCase());
        return fields.some(f => f.includes(t));
      });
    }
    return list;
  }, [items, qStatus, qUTM, qDateFrom, qDateTo, qText]);

  function exportCSV() {
    const csv = toCSV(filtered);
    const now = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    download(`tigo-leads-${now}.csv`, csv);
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      {/* Header */}
      <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/media/TiGoLogo.png" alt="TiGo" className="h-9 w-auto" />
            <div>
              <h1 className="text-2xl font-semibold">Seguimiento de Prospectos</h1>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Leads de suscripción TiGo · CRM interno con filtros, timeline y acciones rápidas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <Download className="h-4 w-4" /> Exportar CSV
            </button>
          </div>
        </div>
      </header>

      {/* Filtros */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-border bg-white p-4 shadow-sm dark:bg-neutral-900">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-end">
            <div className="flex-1">
              <label className="text-xs font-medium mb-1 block">Búsqueda</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                <input
                  value={qText}
                  onChange={e => setQText(e.target.value)}
                  placeholder="Nombre, correo, teléfono, ciudad, UTM…"
                  className="w-full pl-8 pr-3 py-2 rounded-xl border border-border bg-white dark:bg-white text-sm text-neutral-900"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block">Estatus</label>
              <select
                className="rounded-xl border border-border bg-white dark:bg-white text-sm px-3 py-2 text-neutral-900"
                value={qStatus}
                onChange={(e) => setQStatus(e.target.value)}
              >
                <option value="">Todos</option>
                {STATUSES.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block">UTM Source</label>
              <input
                value={qUTM}
                onChange={e => setQUTM(e.target.value)}
                placeholder="ej. facebook, ads, newsletter"
                className="rounded-xl border border-border bg-white dark:bg-white text-sm px-3 py-2 text-neutral-900"
              />
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block">Desde</label>
              <input
                type="date"
                value={qDateFrom}
                onChange={e => setQDateFrom(e.target.value)}
                className="rounded-xl border border-border bg-white dark:bg-white text-sm px-3 py-2 text-neutral-900"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Hasta</label>
              <input
                type="date"
                value={qDateTo}
                onChange={e => setQDateTo(e.target.value)}
                className="rounded-xl border border-border bg-white dark:bg-white text-sm px-3 py-2 text-neutral-900"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tabla */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-3xl border border-border overflow-hidden bg-white dark:bg-neutral-900">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr className="text-left">
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Ubicación</th>
                  <th className="px-4 py-3">UTM</th>
                  <th className="px-4 py-3">Estatus</th>
                  <th className="px-4 py-3">Dueño</th>
                  <th className="px-4 py-3">Próx. acción</th>
                  <th className="px-4 py-3">Creado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-neutral-500">
                      Cargando…
                    </td>
                  </tr>
                )}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-neutral-500">
                      Sin resultados con los filtros actuales.
                    </td>
                  </tr>
                )}

                {filtered.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-border">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.nombre} {r.apellidos}</div>
                        <div className="text-xs text-neutral-500">{r.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.telefono && (
                            <a
                              href={waLink(r.telefono, "Hola, te contacto de TiGo para tu suscripción.")}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 text-accent hover:underline"
                              title="WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4" /> {r.telefono}
                            </a>
                          )}
                        </div>
                        {r.correo && (
                          <a
                            href={mailLink(r.correo)}
                            className="text-xs text-accent hover:underline"
                          >
                            <Mail className="inline h-3 w-3 mr-1" />
                            {r.correo}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>{r.ciudad || "-"}, {r.estado || "-"}</div>
                        <div className="text-xs text-neutral-500">{r.cp || "-"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs">
                          <span className="font-medium">src:</span> {r.utm?.source || "-"}<br/>
                          <span className="font-medium">camp:</span> {r.utm?.campaign || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusSelect lead={r} />
                      </td>
                      <td className="px-4 py-3">
                        <OwnerInline lead={r} />
                      </td>
                      <td className="px-4 py-3">
                        <NextActionInline lead={r} />
                      </td>
                      <td className="px-4 py-3">{formatTS(r.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                          className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                        >
                          {expandedId === r.id ? (
                            <>
                              <ChevronUp className="h-4 w-4" /> Ocultar
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4" /> Detalles
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                    {expandedId === r.id && (
                      <tr className="bg-neutral-50/60 dark:bg-neutral-900/60 border-t border-border">
                        <td colSpan={9} className="px-4 py-4">
                          <LeadDetails lead={r} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {hasMore && (
            <div className="flex justify-end border-t border-border px-4 py-3 bg-neutral-50 dark:bg-neutral-900">
              <button
                onClick={loadMore}
                className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                Cargar más <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/** ======== SUBCOMPONENTES ======== */

function StatusSelect({ lead }) {
  const [saving, setSaving] = useState(false);
  const current = statusMeta[lead.status || "new"];

  async function changeStatus(next) {
    if (next === (lead.status || "new")) return;
    setSaving(true);
    try {
      const ref = doc(getFirestore(), "leads", lead.id);
      await updateDoc(ref, {
        status: next,
        lastActivityAt: serverTimestamp(),
      });
      // Log activity
      await addDoc(collection(ref, "leadActivities"), {
        type: "status_change",
        from: lead.status || "new",
        to: next,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("changeStatus error:", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative inline-block">
      <select
        disabled={saving}
        className={`rounded-full border px-2 py-1 text-xs font-medium ${current?.color || "bg-neutral-100 text-neutral-700 border-neutral-200"}`}
        value={lead.status || "new"}
        onChange={(e) => changeStatus(e.target.value)}
      >
        {STATUSES.map(s => (
          <option key={s.key} value={s.key}>{s.label}</option>
        ))}
      </select>
    </div>
  );
}

function OwnerInline({ lead }) {
  const [val, setVal] = useState(lead.owner || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (val === (lead.owner || "")) return;
    setSaving(true);
    try {
      const ref = doc(getFirestore(), "leads", lead.id);
      await updateDoc(ref, {
        owner: val || null,
        lastActivityAt: serverTimestamp(),
      });
      await addDoc(collection(ref, "leadActivities"), {
        type: "owner_update",
        owner: val || null,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("save owner error:", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="correo o nombre"
        className="w-40 rounded-xl border border-border bg-white dark:bg-white text-sm px-2 py-1 text-neutral-900"
      />
      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
        title="Asignar"
      >
        <UserPlus className="h-3.5 w-3.5" /> Guardar
      </button>
    </div>
  );
}

function NextActionInline({ lead }) {
  const [date, setDate] = useState(() => {
    const d = lead.nextActionAt?.toDate?.() || null;
    return d ? d.toISOString().slice(0, 16) : "";
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const ref = doc(getFirestore(), "leads", lead.id);
      let tsVal = null;
      if (date) tsVal = Timestamp.fromDate(new Date(date));
      await updateDoc(ref, {
        nextActionAt: tsVal,
        lastActivityAt: serverTimestamp(),
      });
      await addDoc(collection(ref, "leadActivities"), {
        type: "next_action",
        nextActionAt: tsVal,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("save nextAction error:", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="datetime-local"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="rounded-xl border border-border bg-white dark:bg-white text-sm px-2 py-1 text-neutral-900"
      />
      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
      >
        <Calendar className="h-3.5 w-3.5" /> Guardar
      </button>
    </div>
  );
}

function LeadDetails({ lead }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Info principal */}
      <div className="rounded-2xl border border-border bg-white p-4 dark:bg-neutral-950">
        <h3 className="font-semibold text-sm mb-2">Información del lead</h3>
        <div className="text-sm space-y-1">
          <Row k="Nombre" v={`${lead.nombre || ""} ${lead.apellidos || ""}`} />
          <Row k="Teléfono" v={lead.telefono || "-"} />
          <Row k="Correo" v={lead.correo || "-"} />
          <Row k="Dirección" v={lead.direccion || "-"} />
          <Row k="Ciudad/Estado" v={`${lead.ciudad || "-"}, ${lead.estado || "-"}`} />
          <Row k="C.P." v={lead.cp || "-"} />
          <Row k="Creado" v={formatTS(lead.createdAt)} />
          <Row k="Owner" v={lead.owner || "-"} />
          <Row k="Estatus" v={statusMeta[lead.status || "new"]?.label || "Nuevo"} />
          <Row k="Próxima acción" v={formatTS(lead.nextActionAt)} />
          <Row k="Última actividad" v={formatTS(lead.lastActivityAt)} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {lead.telefono && (
            <a
              href={waLink(lead.telefono, "Hola, te escribo de TiGo respecto a tu suscripción.")}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          )}
          {lead.correo && (
            <a
              href={mailLink(lead.correo)}
              className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <Mail className="h-4 w-4" /> Email
            </a>
          )}
          {lead.telefono && (
            <a
              href={`tel:${lead.telefono}`}
              className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <Phone className="h-4 w-4" /> Llamar
            </a>
          )}
        </div>

        {/* Notas rápidas */}
        <div className="mt-4">
          <QuickNote leadId={lead.id} />
        </div>
      </div>

      {/* UTM / meta */}
      <div className="rounded-2xl border border-border bg-white p-4 dark:bg-neutral-950">
        <h3 className="font-semibold text-sm mb-2">Origen (UTM)</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Row k="source" v={lead.utm?.source || "-"} />
          <Row k="medium" v={lead.utm?.medium || "-"} />
          <Row k="campaign" v={lead.utm?.campaign || "-"} />
          <Row k="term" v={lead.utm?.term || "-"} />
          <Row k="content" v={lead.utm?.content || "-"} />
        </div>
        <p className="mt-3 text-xs text-neutral-500">
          Referrer: {lead.referrer || "-"}
        </p>
        <p className="text-xs text-neutral-500">
          Página: {lead.page || "-"}
        </p>

        {/* Marcar ganado/perdido rápido */}
        <div className="mt-4 flex gap-2">
          <QuickStatus lead={lead} to="won" icon={<CheckCircle2 className="h-4 w-4" />} label="Marcar Ganado" />
          <QuickStatus lead={lead} to="lost" icon={<XCircle className="h-4 w-4" />} label="Marcar Perdido" />
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-2xl border border-border bg-white p-0 overflow-hidden dark:bg-neutral-950">
        <LeadTimeline leadId={lead.id} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-neutral-500">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

function QuickStatus({ lead, to, icon, label }) {
  const [saving, setSaving] = useState(false);
  async function run() {
    setSaving(true);
    try {
      const ref = doc(getFirestore(), "leads", lead.id);
      await updateDoc(ref, { status: to, lastActivityAt: serverTimestamp() });
      await addDoc(collection(ref, "leadActivities"), {
        type: "status_change",
        from: lead.status || "new",
        to,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("quick status error:", e);
    } finally {
      setSaving(false);
    }
  }
  return (
    <button
      onClick={run}
      disabled={saving}
      className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800"
    >
      {icon} {label}
    </button>
  );
}

function QuickNote({ leadId }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const lref = doc(getFirestore(), "leads", leadId);
      await addDoc(collection(lref, "leadActivities"), {
        type: "note",
        text,
        createdAt: serverTimestamp(),
      });
      await updateDoc(lref, { lastActivityAt: serverTimestamp() });
      setText("");
    } catch (e) {
      console.error("note error:", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <label className="text-xs font-medium mb-1 block">Agregar nota</label>
      <div className="flex items-start gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Resultado de llamada, objeciones, próximos pasos…"
          className="flex-1 rounded-xl border border-border bg-white dark:bg-white text-sm px-3 py-2 text-neutral-900"
        />
        <button
          onClick={save}
          disabled={saving}
          className="h-[38px] inline-flex items-center gap-2 rounded-xl border border-border px-3 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <StickyNote className="h-4 w-4" /> Guardar
        </button>
      </div>
    </div>
  );
}

function LeadTimeline({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = collection(getFirestore(), "leads", leadId, "leadActivities");
    const q = query(ref, orderBy("createdAt", "desc"), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => {
      console.error("timeline error:", e);
      setLoading(false);
    });
    return () => unsub();
  }, [leadId]);

  return (
    <div>
      <div className="border-b border-border px-4 py-3">
        <h3 className="font-semibold text-sm">Timeline de actividades</h3>
      </div>
      <div className="max-h-[420px] overflow-auto p-4 space-y-3">
        {loading && <div className="text-sm text-neutral-500">Cargando timeline…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-neutral-500">Aún no hay actividades para este lead.</div>
        )}
        {items.map((a) => (
          <div key={a.id} className="rounded-xl border border-border p-3 bg-white dark:bg-neutral-950">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-neutral-500">
                {iconByType(a.type)} {labelByType(a.type)}
              </div>
              <div className="text-xs text-neutral-500">{formatTS(a.createdAt)}</div>
            </div>
            {a.text && <p className="mt-1 text-sm">{a.text}</p>}
            {a.type === "status_change" && (
              <p className="mt-1 text-sm">De <b>{a.from}</b> a <b>{a.to}</b></p>
            )}
            {a.type === "next_action" && (
              <p className="mt-1 text-sm">Programado para: <b>{formatTS(a.nextActionAt)}</b></p>
            )}
            {a.owner && a.type === "owner_update" && (
              <p className="mt-1 text-sm">Asignado a <b>{a.owner}</b></p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function iconByType(t) {
  switch (t) {
    case "note": return <StickyNote className="inline h-3.5 w-3.5 mr-1" />;
    case "status_change": return <Sparkles className="inline h-3.5 w-3.5 mr-1" />;
    case "next_action": return <Clock className="inline h-3.5 w-3.5 mr-1" />;
    case "owner_update": return <UserPlus className="inline h-3.5 w-3.5 mr-1" />;
    default: return <StickyNote className="inline h-3.5 w-3.5 mr-1" />;
  }
}
function labelByType(t) {
  switch (t) {
    case "note": return "nota";
    case "status_change": return "cambio de estatus";
    case "next_action": return "próxima acción";
    case "owner_update": return "asignación";
    default: return t || "actividad";
  }
}
